import type React from "react";
import { Group, Stack, Text, Tooltip } from "@mantine/core";
import { Badge, Table } from "@xplatform/design-system";
import { IconStarFilled } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { ReviewStatus, type RowEdit } from "@models/job";
import {
  type MappingResultRow,
  type MappingResultRowOe,
  type OeTarget,
} from "@models/mappingResult";
import type { RowSaveStatus } from "@hooks/useJobResults";
import { useResultRow } from "@hooks/jobResults/useResultRow";
import RevisionHistoryDrawer from "@components/RevisionHistoryDrawer/RevisionHistoryDrawer";
import { PLACEHOLDER, resolveDisplayTarget } from "@utils/rowHelpers";
import RowStatusCell from "../RowStatusCell";
import RowActionsCell, { ExpandToggle } from "../RowActionsCell";
import RowFindingBadge from "../RowFindingBadge";

interface WdJobResultsTableRowProps {
  row: MappingResultRowOe;
  edit: RowEdit | undefined;
  saveState: RowSaveStatus | undefined;
  jobId: string;
  expanded: boolean;
  /** The row's candidates, once its dropdown has been opened. */
  candidates: readonly OeTarget[] | undefined;
  registerRef: (el: HTMLTableRowElement | null) => void;
  onToggleExpand: () => void;
  onAccept: () => void;
  onReject: () => void;
  onRecover: () => void;
  onRetrySave: () => void;
  onRowRestored: (row: MappingResultRow) => void;
}

/**
 * One OE row: the part as the supplier named it, and the catalogue component
 * it resolved to.
 *
 * A catalogue MISS is the case this row is shaped around. The backend leaves
 * BOTH catalogue fields empty on one and puts the model's proposal in
 * `suggestedText` — so the id column stays blank (that emptiness IS the
 * finding) and the text column shows the proposal, marked as a proposal.
 * `isProposal` is what decides that, never the row's `mappingStatus`: the
 * latter is the pipeline's verdict and never changes, so a row the reviewer
 * has since corrected onto a real entry would keep being flagged.
 */
const WdJobResultsTableRow: React.FC<WdJobResultsTableRowProps> = ({
  row,
  edit,
  saveState,
  jobId,
  expanded,
  candidates,
  registerRef,
  onToggleExpand,
  onAccept,
  onReject,
  onRecover,
  onRetrySave,
  onRowRestored,
}) => {
  const { t } = useTranslation();
  const {
    historyOpen,
    setHistoryOpen,
    effectiveStatus,
    isFailed,
    isSaving,
    actionsDisabled,
    hasAlternative,
  } = useResultRow(row, edit, saveState);

  const { source } = row;
  const target = resolveDisplayTarget(row, edit, candidates);
  const isProposal = target?.isProposal ?? false;

  const rowClass = `job-results-page__row${
    expanded ? " job-results-page__row--expanded" : ""
  }`;

  const dimmed = (
    <Text size="xs" c="dimmed">
      {PLACEHOLDER}
    </Text>
  );

  return (
    <>
      <Table.Tr
        ref={registerRef}
        className={rowClass}
        tabIndex={0}
        data-row-index={row.rowIndex}
      >
        <Table.Td>
          <Text size="sm" c="dimmed" ff="monospace" fw={500}>
            {row.rowIndex + 1}
          </Text>
        </Table.Td>

        {/* Source · OE Number */}
        <Table.Td>
          {source.oeNumber ? (
            <Text size="sm" ff="monospace">
              {source.oeNumber}
            </Text>
          ) : (
            dimmed
          )}
        </Table.Td>

        {/* Source · OE Name */}
        <Table.Td>
          {isFailed ? (
            <Text size="xs" c="red" lineClamp={2}>
              {row.error?.message ?? t("resultRow.status.failed")}
            </Text>
          ) : source.oeName ? (
            <Text size="sm" lineClamp={3}>
              {source.oeName}
            </Text>
          ) : (
            dimmed
          )}
        </Table.Td>

        {/* Source · OE Function */}
        <Table.Td>
          {source.oeFunction ? (
            <Text size="sm" lineClamp={2}>
              {source.oeFunction}
            </Text>
          ) : (
            dimmed
          )}
        </Table.Td>

        {/* Source · OE Domain */}
        <Table.Td>
          {source.oeDomain ? (
            <Text size="sm" lineClamp={2}>
              {source.oeDomain}
            </Text>
          ) : (
            dimmed
          )}
        </Table.Td>

        {/* Suggested · ItemMP ID — blank on a proposal, which has no
            catalogue row to point at. */}
        <Table.Td>
          {target?.itemMpId != null ? (
            <Text size="sm" ff="monospace" ta="left">
              {target.itemMpId}
            </Text>
          ) : (
            dimmed
          )}
        </Table.Td>

        {/* Suggested · ItemMP Text — or the model's proposal, said to be one.
            Under it, what the pipeline concluded about this suggestion.

            The finding sits HERE, not in the Status column, because it judges
            this cell: "Require Review" and "Exact Match" are both claims about
            the entry named above them, and a verdict two cells away from the
            thing it judges is one the reviewer has to go looking for. It
            renders a size down from the Status badge — the name is the
            content, the finding is a footnote to it.

            On a proposal the AI marker REPLACES the finding rather than joining
            it. A catalogue miss with a proposed name says one thing — "nothing
            matched, so AI suggested this" — and the marker already says it. */}
        <Table.Td>
          {isProposal ? (
            <Stack gap={2} align="flex-start">
              <Tooltip
                multiline
                w={280}
                label={t("jobResultsPage.wdProposedTooltip", {
                  defaultValue:
                    "Not in the database. This is the name AI suggests the entry should have — a proposal, not a stored record.",
                })}
              >
                <Text size="sm" ta="left" lineClamp={2} fs="italic">
                  {target?.suggestedText}
                </Text>
              </Tooltip>
              <Badge
                size="xs"
                variant="light"
                color="violet"
                leftSection={<IconStarFilled size={10} />}
              >
                {t("jobResultsPage.aiSuggested", {
                  defaultValue: "AI suggested",
                })}
              </Badge>
            </Stack>
          ) : target?.itemMpText ? (
            <Stack gap={2} align="flex-start">
              <Text size="sm" ta="left" lineClamp={2}>
                {target.itemMpText}
              </Text>
              <RowFindingBadge row={row} />
            </Stack>
          ) : (
            dimmed
          )}
        </Table.Td>

        {/* Status — the REVIEWER axis only. The pipeline's finding renders
            under the suggestion it is about; see the ItemMP Text cell above. */}
        <RowStatusCell
          status={effectiveStatus}
          saveState={saveState}
          onRetrySave={onRetrySave}
        />

        <RowActionsCell
          expanded={expanded}
          totalAlts={hasAlternative ? 1 : 0}
          isFailed={isFailed}
          isSaving={isSaving}
          actionsDisabled={actionsDisabled}
          hasTopMatch={target !== null}
          expandColor="blue"
          showExpand={false}
          showMenu={false}
          isRejected={effectiveStatus === ReviewStatus.Rejected}
          onToggleExpand={onToggleExpand}
          onAccept={onAccept}
          onReject={onReject}
          onOpenHistory={() => setHistoryOpen(true)}
          onRecover={onRecover}
        />

        {/* Dropdown · candidate expand toggle */}
        <Table.Td>
          <Group gap={4} wrap="nowrap" justify="center">
            <ExpandToggle
              expanded={expanded}
              totalAlts={hasAlternative ? 1 : 0}
              isFailed={isFailed}
              expandColor="blue"
              variant="transparent"
              onToggleExpand={onToggleExpand}
            />
          </Group>
        </Table.Td>
      </Table.Tr>

      <RevisionHistoryDrawer
        opened={historyOpen}
        jobId={jobId}
        rowIndex={row.rowIndex}
        onRestored={onRowRestored}
        onClose={() => setHistoryOpen(false)}
      />
    </>
  );
};

export default WdJobResultsTableRow;
